import { useState } from 'react'
import { Auswahlfeld } from './Auswahlfeld'
import type { Auswahleintrag } from './Auswahlfeld'
import { Ordnerbaum, nimmtAuf } from './Ordnerbaum'
import { anzahlwort, gesellschaftsname, t } from './sprache'
import type { Belegordner, Datenimportstand } from './typen'

/**
 * Die Datenimporte im Ordnerbaum des Buchungsjournals (5.1 Abschnitt 3.45,
 * R970–R972).
 *
 * **Derselbe Baum, keine zweite Ablage.** Ein Datenimport ist ein Beleg, und
 * der Baum ist der des Buchungsjournals: Wer hier einen Ordner wählt, sieht
 * dieselben Ordner wie dort, und wer hier verschiebt, findet den Datenimport
 * im Journal am selben Ort.
 *
 * **Der Jahresordner ist kein gespeicherter Ort** (R984): Ein Datenimport, den
 * kein eigener Ordner hält, liegt im Jahresordner seines Geschäftsjahres.
 *
 * **Und der Baum ist derselbe, mit derselben Bedienung** (R980): Rechtsklick,
 * Tasten und Ziehen kommen aus `Ordnerbaum` — hier wird seit dem 2026-10-03
 * nicht nur abgelegt, sondern auch gegliedert.
 */

/** Was links gewählt ist — und damit, was rechts steht. */
export type Ordnerwahl =
  | { art: 'alle' }
  | { art: 'gesellschaft'; einheit: string }
  | { art: 'ordner'; einheit: string; id: number }

export const ALLE: Ordnerwahl = { art: 'alle' }

/** Die Ordner je Gesellschaft, wie sie der Rechenkern liefert. */
export type Ordnervorrat = Record<string, Belegordner[]>

/** Ob ein Datenimport in genau diesem Ordner liegt — nicht in einem darunter. */
export function liegtIn(s: Datenimportstand, o: Belegordner): boolean {
  if (s.einheit !== o.einheit) {
    return false
  }
  return o.istEingang ? s.ordner == null && nimmtAuf(o, [s.periode]) : s.ordner === o.id
}

/** Ob ein Datenimport zu dem gehört, was links gewählt ist. */
export function passtZurWahl(s: Datenimportstand, wahl: Ordnerwahl, vorrat: Ordnervorrat): boolean {
  switch (wahl.art) {
    case 'alle':
      return true
    case 'gesellschaft':
      return s.einheit === wahl.einheit
    case 'ordner': {
      const o = (vorrat[wahl.einheit] ?? []).find((x) => x.id === wahl.id)
      return o !== undefined && liegtIn(s, o)
    }
  }
}

/**
 * Die Ordner einer Gesellschaft in der Reihenfolge des Baums, mit ihrer Tiefe.
 *
 * Eine Formulierung für den Baum und für die Auswahl beim Verschieben: Beide
 * zeigen dieselben Ordner in derselben Folge.
 */
export function baumfolge(ordner: Belegordner[]): { ordner: Belegordner; tiefe: number }[] {
  const zweig = (eltern: number | null, tiefe: number): { ordner: Belegordner; tiefe: number }[] =>
    ordner
      .filter((o) => o.eltern === eltern)
      .flatMap((o) => [{ ordner: o, tiefe }, ...zweig(o.id, tiefe + 1)])
  return zweig(null, 0)
}

/** Der Name eines Ordners mit seinen übergeordneten — „2026 › Abschluss › Abgrenzungen". */
export function ordnerpfad(ordner: Belegordner[], id: number): string {
  const teile: string[] = []
  let o = ordner.find((x) => x.id === id)
  while (o) {
    teile.unshift(o.name)
    const eltern = o.eltern
    o = eltern === null ? undefined : ordner.find((x) => x.id === eltern)
  }
  return teile.join(' › ')
}

/**
 * Wie viele Datenimporte in einem Ordner liegen — **mit seinen Unterordnern**,
 * wie im Buchungsjournal: Ein Ordner, dessen Inhalt darunter liegt, sähe sonst
 * leer aus.
 */
export function imTeilbaum(staende: Datenimportstand[], ordner: Belegordner[], id: number): number {
  const o = ordner.find((x) => x.id === id)
  if (!o) {
    return 0
  }
  return ordner
    .filter((x) => x.eltern === id)
    .reduce((summe, kind) => summe + imTeilbaum(staende, ordner, kind.id), staende.filter((s) => liegtIn(s, o)).length)
}

/** Die Gesellschaften, die Datenimporte tragen — nach ihrem Namen, nicht nach der Kennung (R838). */
export function gesellschaftenMitImporten(staende: Datenimportstand[]): string[] {
  return [...new Set(staende.map((s) => s.einheit))].sort((a, b) =>
    gesellschaftsname(a).localeCompare(gesellschaftsname(b)),
  )
}

/**
 * Links: alle Datenimporte, darunter jede Gesellschaft, darunter ihre Ordner.
 *
 * **Die erste Ebene ist die Gesellschaft**, wie im Buchungsjournal (R373):
 * Ein Ordner gehört genau einer.
 */
export function Importordnerbaum({
  staende,
  vorrat,
  wahl,
  aufWahl,
  aufGeaendert,
  aufBelegeGezogen,
}: {
  staende: Datenimportstand[]
  vorrat: Ordnervorrat
  wahl: Ordnerwahl
  aufWahl: (wahl: Ordnerwahl) => void
  /** Nach jeder Änderung am Baum: neu laden. */
  aufGeaendert: () => void
  /** Datenimporte wurden auf einen Ordner gezogen (R983). */
  aufBelegeGezogen: (ids: number[], ziel: number) => Promise<void>
}) {
  function eintrag(
    schluessel: string,
    gewaehlt: boolean,
    tiefe: number,
    name: string,
    anzahl: number,
    neu: Ordnerwahl,
  ) {
    return (
      <li key={schluessel}>
        <button
          type="button"
          className={`ordnereintrag${gewaehlt ? ' gewaehlt' : ''}`}
          style={{ paddingLeft: `${8 + tiefe * 16}px` }}
          aria-current={gewaehlt ? 'true' : undefined}
          onClick={() => aufWahl(neu)}
        >
          {Array.from({ length: tiefe }, (_, i) => (
            <span key={i} className="einrueckung" aria-hidden="true" />
          ))}
          <span className="name">{name}</span>
          <span className="nummer">{anzahlwort(anzahl)}</span>
        </button>
      </li>
    )
  }

  return (
    <nav className="ordnerbaum" aria-label={t('ordner.titel')}>
      <h3>{t('ordner.titel')}</h3>
      <ul>
        {eintrag('alle', wahl.art === 'alle', 0, t('import.ordner.alle'), staende.length, ALLE)}
        {gesellschaftenMitImporten(staende).flatMap((einheit) => {
          const ordner = vorrat[einheit] ?? []
          return [
            eintrag(
              `g:${einheit}`,
              wahl.art === 'gesellschaft' && wahl.einheit === einheit,
              0,
              gesellschaftsname(einheit),
              staende.filter((s) => s.einheit === einheit).length,
              { art: 'gesellschaft', einheit },
            ),
            <Ordnerbaum
              key={`b:${einheit}`}
              einheit={einheit}
              ordner={ordner}
              gewaehlt={wahl.art === 'ordner' && wahl.einheit === einheit ? wahl.id : null}
              aufWahl={(id) => aufWahl({ art: 'ordner', einheit, id })}
              zahl={(o) => anzahlwort(imTeilbaum(staende, ordner, o.id))}
              versatz={1}
              aufGeaendert={aufGeaendert}
              aufBelegeGezogen={aufBelegeGezogen}
            />,
          ]
        })}
      </ul>
    </nav>
  )
}

/**
 * Über der Liste, solange etwas markiert ist: wie viele, und wohin damit.
 *
 * **Nur in einen Ordner ihrer Gesellschaft** (R971). Sind Datenimporte
 * mehrerer Gesellschaften markiert, gibt es keinen gemeinsamen Ordner — dann
 * sagt die Leiste das, statt eine leere Auswahl anzubieten.
 */
export function Verschiebeleiste({
  markiert,
  vorrat,
  laeuft,
  aufVerschieben,
  aufAufheben,
}: {
  markiert: Datenimportstand[]
  vorrat: Ordnervorrat
  laeuft: boolean
  aufVerschieben: (ordner: number) => void
  aufAufheben: () => void
}) {
  const [ziel, setZiel] = useState('')
  const einheiten = [...new Set(markiert.map((s) => s.einheit))]
  const einheit = einheiten.length === 1 ? einheiten[0]! : null
  const ordner = einheit ? (vorrat[einheit] ?? []) : []

  // **Nur Ordner ihres Jahres** (R985): Ein Datenimport aus 2025 geht nicht
  // in „2026 › Prüfung" — angeboten wird, was ihn nimmt.
  const perioden = markiert.map((s) => s.periode)
  const eintraege: Auswahleintrag[] = baumfolge(ordner)
    .filter(({ ordner: o }) => nimmtAuf(o, perioden))
    .map(({ ordner: o }) => ({
      wert: String(o.id),
      text: ordnerpfad(ordner, o.id),
    }))

  return (
    <div className="verschiebeleiste">
      <span className="menge">{t('import.ordner.markiert', { anzahl: anzahlwort(markiert.length) })}</span>

      {einheit === null ? (
        <span className="erlaeuterung">{t('import.ordner.mehrere')}</span>
      ) : (
        <>
          <label>
            <span className="name">{t('import.ordner.ziel')}</span>
            <Auswahlfeld
              wert={eintraege.some((e) => e.wert === ziel) ? ziel : ''}
              eintraege={eintraege}
              aufWahl={setZiel}
              leer="—"
              beschriftung={t('import.ordner.ziel')}
              sorte="aufzaehlung"
              abgeschaltet={laeuft}
            />
          </label>
          <button
            type="button"
            disabled={laeuft || !eintraege.some((e) => e.wert === ziel)}
            onClick={() => aufVerschieben(Number(ziel))}
          >
            {t('import.ordner.verschieben')}
          </button>
        </>
      )}

      <button type="button" className="schmal" disabled={laeuft} onClick={aufAufheben}>
        {t('import.ordner.aufheben')}
      </button>
    </div>
  )
}
